import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Collapse,
} from '@mui/material';
import ReactDataSheet from 'react-datasheet';
import { useAuth } from '../components/AuthContext';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { API_BASE_URL } from '../components/api.js';

const DIAS_CURTO = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function PreverPrevisao() {
  const { authenticatedFetch } = useAuth();

  const hoje = new Date();
  const [mes,           setMes]          = useState(hoje.getMonth() + 1);
  const [ano,           setAno]          = useState(hoje.getFullYear());
  const [grid,          setGrid]         = useState([]);
  const [loading,       setLoading]      = useState(false);
  const [feedback,      setFeedback]     = useState(null); // { type, msg, detalhe }

  useEffect(() => {
    setFeedback(null);
    fetchPrevisaoExistente();
  }, [mes, ano]);

  // ── Busca previsão existente ──────────────────────────────────────────────
  const fetchPrevisaoExistente = async () => {
    try {
      const res = await authenticatedFetch(
        `${API_BASE_URL}/api/prever-previsao-buscar/?mes=${mes}&ano=${ano}`
      );
      if (res.ok) {
        const data = await res.json();
        setGrid(data.length > 0 ? convertToGrid(data) : []);
      }
    } catch (err) {
      console.error('Erro ao buscar previsão:', err);
    }
  };

  // ── Gerar previsão ────────────────────────────────────────────────────────
  const handleGerarPrevisao = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const res = await authenticatedFetch(
        `${API_BASE_URL}/api/prever-previsao-criar/?mes=${mes}&ano=${ano}`,
        { method: 'POST' }
      );
      const data = await res.json();

      if (res.ok) {
        // Monta detalhe sobre dias provisórios que foram estimados
        const ma = data.mes_anterior;
        let detalhe = null;
        if (ma?.dias_inseridos > 0) {
          const lista = ma.dias_faltantes
            .map(d => `dia ${d.dia} (${['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'][d.weekday]}) → ${d.paes_provisorio} pães`)
            .join(' | ');
          detalhe = `${ma.mes_ref}: ${ma.dias_inseridos} dia(s) estimado(s) com média histórica — ${lista}`;
        } else if (ma) {
          detalhe = `${ma.mes_ref}: ${ma.status}`;
        }
        setFeedback({ type: 'success', msg: data.message, detalhe });
        await fetchPrevisaoExistente();
      } else {
        setFeedback({ type: 'error', msg: data.error || 'Erro ao gerar previsão.' });
      }
    } catch {
      setFeedback({ type: 'error', msg: 'Erro na requisição.' });
    } finally {
      setLoading(false);
    }
  };

  // ── Converte dados para a grid ────────────────────────────────────────────
  const convertToGrid = (data) => {
    const header = [
        { value: 'Dia',          readOnly: true, width: 60  },
        { value: 'Data',         readOnly: true, width: 90  },
        { value: 'Média 2sem',   readOnly: true, width: 90  },
        { value: 'Feriado',      readOnly: true, width: 70  },
        { value: 'Pães Prev.',   readOnly: true, width: 90  },
        { value: 'Ticket',       readOnly: true, width: 100 },
        { value: 'Fat. Prev.',   readOnly: true, width: 120 },
    ];

    const rows = data.map(item => [
        { value: DIAS_CURTO[item.dia_semana], readOnly: true },
        { value: item.data.split('-').reverse().slice(0, 2).join('/'), readOnly: true },
        { value: Math.round(item.media_paes_2sem || 0), readOnly: true },
        { value: Number(item.coef_fatfer || 1).toFixed(3), readOnly: true },
        { value: Math.round(item.paes_previstos), readOnly: true, style: { fontWeight: 'bold', color: '#1976d2' } },
        { value: Number(item.ticket_medio_prev || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), readOnly: true },
        { value: Number(item.faturamento_previsto || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), readOnly: true, style: { fontWeight: 'bold', backgroundColor: '#f0f7ff' } },
    ]);

    return [header, ...rows];
  };

  const gridStyle = {
    '& .cell':      { border: '1px solid #ccc', padding: '8px', textAlign: 'center' },
    '& .read-only': { background: '#fff', color: '#333' },
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 900, p: 2, ...gridStyle }}>

      {/* Barra de controles ────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
        mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2,
      }}>

        {/* Seletor Mês */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Mês</InputLabel>
          <Select value={mes} label="Mês" onChange={(e) => setMes(e.target.value)}>
            {[...Array(12)].map((_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {new Date(0, i).toLocaleString('pt-BR', { month: 'long' })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Seletor Ano */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Ano</InputLabel>
          <Select value={ano} label="Ano" onChange={(e) => setAno(e.target.value)}>
            {[2024, 2025, 2026, 2027].map((a) => (
              <MenuItem key={a} value={a}>{a}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Botão Gerar Previsão */}
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <QueryStatsIcon />}
          onClick={handleGerarPrevisao}
          disabled={loading}
        >
          {loading ? 'Gerando...' : 'Gerar Previsão'}
        </Button>
      </Box>

      {/* Feedback ──────────────────────────────────────────────────────────── */}
      <Collapse in={!!feedback}>
        {feedback && (
          <Alert
            severity={feedback.type}
            onClose={() => setFeedback(null)}
            sx={{ mb: 2 }}
          >
            {feedback.msg}
            {feedback.detalhe && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
                {feedback.detalhe}
              </Typography>
            )}
          </Alert>
        )}
      </Collapse>

      {/* Título */}
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        Previsão: {new Date(ano, mes - 1).toLocaleString('pt-BR', { month: 'long' })}/{ano}
      </Typography>

      {/* Tabela ─────────────────────────────────────────────────────────────── */}
      <Box sx={{ border: '1px solid #ddd', height: '100%', overflow: 'auto', bgcolor: 'white' }}>
        {grid.length > 1 ? (
          <ReactDataSheet
            data={grid}
            valueRenderer={(cell) => cell.value}
            sheetRenderer={(props) => (
              <table className={props.className + ' data-grid-container'}>
                <thead>
                  <tr>
                    {grid[0].map((h, i) => (
                      <th key={i} style={{
                        width: h.width, padding: '8px',
                        background: '#37474f', color: '#fff',
                        border: '1px solid #263238', textAlign: 'center',
                        fontSize: 12, whiteSpace: 'nowrap',
                      }}>
                        {h.value}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{props.children.slice(1)}</tbody>
              </table>
            )}
          />
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', color: '#999' }}>
            Nenhuma previsão gerada para este período. Clique em "Gerar Previsão".
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PreverPrevisao;